import { useCallback, useEffect, useRef, useState } from "react"

import { storageEventKey } from "@shared/constants/storage"

import type { StorageStrategy, UseStorageOptions } from "./StorageStrategy"
import { jsonSerializer } from "./StorageStrategy"

/**
 * Unified React hook for reading/writing values to any {@link StorageStrategy}.
 *
 * Handles state initialization, serialization, re-reads on key/strategy changes,
 * updater-function setters, and optional cross-tab / same-tab sync.
 *
 * Prefer using the thin wrapper hooks (`useLocalState`, `useSessionState`, `useCookie`)
 * instead of calling `useStorage` directly.
 *
 * @param strategy  The storage backend to use.
 * @param key       Unique storage key.
 * @param initialValue  Fallback when the key is absent or unreadable.
 * @param options   Optional serializer and sync settings.
 *
 * @example
 * ```ts
 * const [value, setValue] = useStorage(localStorageStrategy, "myKey", 42)
 *
 * setValue(100)            // direct value
 * setValue(prev => prev+1) // updater function
 * setValue(undefined)      // clear — resets to initialValue
 * ```
 */
export function useStorage<T>(
  strategy: StorageStrategy,
  key: string,
  initialValue: T,
  options?: UseStorageOptions<T>
): [T, (value: T | ((prev: T) => T) | undefined) => void] {
  const serializer = options?.serializer ?? jsonSerializer
  const sync = options?.sync ?? false
  const eventKey = storageEventKey(key)

  const readStorage = useCallback((): T => {
    try {
      const raw = strategy.getItem(key)
      return raw !== undefined ? serializer.from(raw) : initialValue
    } catch (error) {
      console.warn(`Error reading storage value for key "${key}":`, error)
      return initialValue
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initialValue is a default, not a dependency
  }, [key, strategy, serializer])

  const [storedValue, setStoredValue] = useState<T>(readStorage)

  // Keep a ref in sync so setValue can read the latest value without
  // needing storedValue in its dependency array (keeps the setter stable)
  const storedValueRef = useRef(storedValue)
  storedValueRef.current = storedValue

  // Re-read storage when key or strategy changes
  useEffect(() => {
    setStoredValue(readStorage())
  }, [readStorage])

  const setValue = useCallback(
    (value: T | ((prev: T) => T) | undefined) => {
      if (value === undefined) {
        strategy.deleteItem(key)
        setStoredValue(initialValue)
        return
      }

      const valueToStore = value instanceof Function ? value(storedValueRef.current) : value
      strategy.setItem(key, serializer.to(valueToStore))
      setStoredValue(valueToStore)

      if (sync) {
        window.dispatchEvent(new CustomEvent(eventKey, { detail: valueToStore }))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initialValue is a default, not a dependency
    [key, strategy, serializer, sync, eventKey]
  )

  // Sync across hook instances (same-tab via CustomEvent, cross-tab via storage event)
  useEffect(() => {
    if (!sync) {
      return
    }

    const handleCustomEvent = (e: CustomEvent<T>) => {
      setStoredValue(e.detail)
    }

    const handleStorageEvent = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          setStoredValue(serializer.from(e.newValue))
        } catch (error) {
          console.warn(`Error parsing storage value for key "${key}":`, error)
        }
      }
    }

    window.addEventListener(eventKey, handleCustomEvent as EventListener)
    window.addEventListener("storage", handleStorageEvent)

    return () => {
      window.removeEventListener(eventKey, handleCustomEvent as EventListener)
      window.removeEventListener("storage", handleStorageEvent)
    }
  }, [key, eventKey, serializer, sync])

  return [storedValue, setValue]
}
